import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { ArrowRight, Check, Mic, CalendarDays, Fingerprint, History, PlugZap, ChevronDown } from "lucide-react";
import { SiApple, SiGoogleplay, SiGooglegemini } from "react-icons/si";
import { useLanguage } from "@/i18n/LanguageProvider";
import { assetLangFor, LANGUAGES, type Lang } from "@/i18n/locale";
import { analytics } from "@/lib/analytics";
import { metaTrack } from "@/lib/metaPixel";
import { LandingCarousel } from "@/components/landing/LandingCarousel";
import { LandingExperience } from "@/components/landing/LandingExperience";
import { LandingActionDemo, LandingConnections, LandingCreator, LandingControl } from "@/components/landing/LandingConnectedLife";
import { LandingPlans } from "@/components/landing/LandingPlans";
import { APP_STORE_URL } from "@/config/landingPricing";
import { supabase } from "@/integrations/supabase/client";
import Footer from "@/components/layout/Footer";
import vyvIcon from "@/assets/vyv-icon.png";
import "@/components/landing/landing-premium.css";
import "@/components/landing/landing-connected.css";

// Public web landing only. Native root routing remains owned by App.tsx.
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.aivyv";

function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className} style={{ color: "#25D366" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 005.71 1.447h.005c6.585 0 11.946-5.335 11.949-11.893a11.821 11.821 0 00-3.484-8.413z" />
    </svg>
  );
}

// Official OpenAI logomark (blossom/knot). simple-icons dropped OpenAI, so this
// is inlined from OpenAI's brand mark, unmodified path. Monochrome via
// currentColor — inherits the band's muted foreground, never recolored.
function OpenAIGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8298-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8297 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8297-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  );
}


export default function Landing() {
  const navigate = useNavigate();
  const { lang, setLang, t, tf } = useLanguage();
  // Public-page metadata follows the chosen language and is restored on exit.
  useEffect(() => {
    const previousTitle = document.title;
    const tags = [
      { selector: 'meta[name="description"]', value: t("landing.v6.hero.lead") },
      { selector: 'meta[property="og:title"]', value: t("landing.v6.hero.title1") + " | VYV" },
      { selector: 'meta[property="og:description"]', value: t("landing.v6.hero.lead") },
    ].map(({ selector, value }) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      const previous = element?.content;
      if (element) element.content = value;
      return { element, previous };
    });
    document.title = t("landing.v6.hero.title1") + " | VYV";
    return () => {
      document.title = previousTitle;
      tags.forEach(({ element, previous }) => { if (element && previous !== undefined) element.content = previous; });
    };
  }, [t]);

  // Fire once per mount — cold-traffic top-of-funnel event.
  useEffect(() => {
    analytics.landingView();
    // Meta mirror: cold-traffic content view (ads land here).
    metaTrack("ViewContent", { content_name: "landing" });

    // CTWA funnel closer (14/07): a visit tagged utm_source=whatsapp comes
    // from the WhatsApp onboarding reply (campaign C1 / wa_quota). Store the
    // SAME durable whatsapp intent the landing WhatsApp CTA uses
    // (onOpenWebApp below): the /app gate drops the query string, so
    // localStorage carries it across signup — MirrorHome consumes it ONCE and
    // spotlights the "Ativar WhatsApp" card, whose tap mints the link code.
    // Same 30-min freshness rule as the CTA path; no new mechanism.
    try {
      if (new URLSearchParams(window.location.search).get("utm_source") === "whatsapp") {
        localStorage.setItem("vyv_intent", JSON.stringify({ intent: "whatsapp", ts: Date.now() }));
      }
    } catch (e) {
      console.error("[landing] whatsapp intent persist failed:", e);
    }
  }, []);

  // Mid-funnel: fire `landing_pricing_viewed` once when the Pricing section
  // scrolls into view. Distinct event from the in-app `paywall_viewed` so the
  // real-purchase paywall metric stays clean. Guarded to fire at most once.
  const pricingRef = useRef<HTMLElement | null>(null);
  const pricingViewedRef = useRef(false);
  useEffect(() => {
    const el = pricingRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !pricingViewedRef.current) {
          pricingViewedRef.current = true;
          analytics.landingPricingViewed();
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-forward already-logged-in WEB visitors to the app. WEB ONLY and native-
  // safe by construction: App.tsx routes "/" to the native app flow when
  // Capacitor.isNativePlatform() is true, so <Landing/> never mounts on native
  // and this session check never runs there — it cannot touch or delay native
  // boot. The explicit guard below is belt-and-suspenders + self-documenting.
  // Cold (logged-out) traffic is the priority, so the landing paints immediately
  // and we only redirect in the BACKGROUND when a session is found — the first
  // paint is never blocked on the session check.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    let active = true;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active && data.session) navigate("/app", { replace: true });
      })
      .catch(() => {
        /* no session / lookup failed → stay on the public landing */
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  // Poster art is named by ISO-639-1 code (/posters/poster-<loc>-*).
  // Qual idioma herda qual asset é UMA tabela, em @/i18n/locale — a regra
  // estava duplicada aqui e no SplashHero, e ganhou uma terceira faixa
  // (fr/it/de herdam inglês) que as duas cópias não teriam como manter iguais.
  const posterLoc = assetLangFor(lang);

  const chooseLang = (code: Lang) => {
    analytics.languageSelected(code);
    setLang(code);
  };

  const onDownload = (placement: string) => analytics.ctaDownloadClick(placement);
  const onOpenWebApp = (placement: string, intent?: string) => {
    analytics.ctaWebappClick(placement);
    // Meta mirror: the "Ativar WhatsApp" CTA is the landing's lead moment
    // (routes into the web app with whatsapp intent — no wa.me on landing).
    if (intent === "whatsapp") {
      metaTrack("Lead", { content_name: placement });
    }
    if (intent) {
      // Durable intent: the /app gate (AppEntry) redirects through /auth → /mirror
      // and drops the query string, so a URL param alone never reaches Home. We
      // persist a timestamped flag that Home consumes ONCE to spotlight the
      // matching card; the timestamp lets Home ignore a stale flag from an
      // unrelated later session. The ?intent stays on the URL as a best-effort
      // fallback for the (rare) already-signed-in direct hop to /home.
      try {
        localStorage.setItem("vyv_intent", JSON.stringify({ intent, ts: Date.now() }));
      } catch { /* private mode — degrade to the URL param */ }
      navigate("/app?intent=" + encodeURIComponent(intent));
      return;
    }
    navigate("/app");
  };

  return (
    <div className="vyv-landing h-full overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: "touch" }}>
      <a className="landing-skip" href="#landing-main">{t("landing.v3.skip")}</a>
      <header className="landing-header sticky top-0 z-30 w-full backdrop-blur-md">
        <div className="landing-header-inner mx-auto flex items-center justify-between gap-4 px-5 sm:px-8">
          <a href="#landing-main" className="landing-brand" aria-label="VYV">
            <img src={vyvIcon} alt="" width={30} height={30} />
            <span>VYV</span>
          </a>
          <nav className="landing-nav" aria-label={t("nav.menu")}>
            <a href="#experience">{t("landing.v3.nav.experience")}</a>
            <a href="#connections">{t("landing.v6.nav.connections")}</a>
            <a href="#plans">{t("landing.pricing.kicker")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <select className="landing-language" aria-label={t("welcome.languageSelector")} value={lang} onChange={(e) => chooseLang(e.target.value as Lang)}>
              {LANGUAGES.map((option) => <option key={option.code} value={option.code}>{option.shortLabel}</option>)}
            </select>
            <button className="landing-nav-cta" onClick={() => onOpenWebApp("nav")}>{t("landing.v6.hero.cta")}<ArrowRight size={16} aria-hidden="true" /></button>
          </div>
        </div>
        <nav className="landing-mobile-nav" aria-label={t("nav.menu")}>
          <a href="#experience">{t("landing.v3.nav.experience")}</a>
          <a href="#connections">{t("landing.v6.nav.connections")}</a>
          <a href="#plans">{t("landing.pricing.kicker")}</a>
        </nav>
      </header>
      <main id="landing-main" tabIndex={-1}>
        <section className="landing-hero mx-auto px-5 sm:px-8" aria-labelledby="landing-title">
          <div className="landing-hero-grid">
            <div>
              <p className="landing-eyebrow flex items-center gap-2"><span className="landing-status-dot" />{t("landing.v6.hero.eyebrow")}</p>
              <h1 id="landing-title" className="font-display">{t("landing.v6.hero.title1")}<span>{t("landing.v6.hero.title2")}</span></h1>
              <p className="landing-lead">{t("landing.v6.hero.lead")}</p>
              <div className="landing-hero-actions"><button className="landing-primary" onClick={() => onOpenWebApp("hero")}>{t("landing.v6.hero.cta")}<ArrowRight size={18} aria-hidden="true" /></button><a href="#connections">{t("landing.v6.hero.explore")}<ChevronDown size={16} aria-hidden="true" /></a></div>
              <div id="download" className="landing-downloads">
                <a className="landing-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => onDownload("hero")}><SiApple size={23} aria-hidden="true" />{t("landing.hero.ctaDownload")}</a>
                <a className="landing-store" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => onDownload("hero_play")}><SiGoogleplay size={21} aria-hidden="true" />{t("landing.hero.ctaPlayStore")}</a>
              </div>
              <div className="landing-hero-links">
                <a className="landing-hero-plans" href="#plans">{t("landing.market.heroLink")}<ChevronDown size={16} aria-hidden="true" /></a>
              </div>
              <div className="landing-trust"><span><Check size={14} aria-hidden="true" />{t("landing.hero.microcopy")}</span></div>
              <p className="landing-powered"><strong>VYV</strong> {t("landing.hero.poweredByConnector")} OpenAI</p>
            </div>
            <LandingActionDemo />
          </div>
        </section>

        <LandingConnections />

        <section id="how-it-works" className="landing-section" aria-labelledby="how-title">
          <div className="landing-section-heading"><p className="landing-eyebrow">{t("landing.what.kicker")}</p><h2 id="how-title">{t("landing.v3.how.title")}</h2><p>{t("landing.v3.how.body")}</p></div>
          <div className="landing-steps">
            {[PlugZap, Mic, Check].map((Icon, i) => <article key={i} className="landing-step"><div className="landing-step-top"><Icon size={23} aria-hidden="true" /><span>0{i + 1}</span></div><h3>{t(`landing.v6.step${i + 1}.title`)}</h3><p>{t(`landing.v6.step${i + 1}.body`)}</p></article>)}
          </div>
        </section>

        <LandingCreator onStart={() => onOpenWebApp("higgsfield_section")} />

        <section id="experience" className="landing-section landing-experience" aria-labelledby="experience-title">
          <div className="landing-experience-copy"><p className="landing-eyebrow">{t("landing.v3.nav.experience")}</p><h2 id="experience-title">{t("landing.v4.experience.title")}</h2><p className="landing-section-description">{t("landing.v4.experience.body")}</p>
            <div className="landing-benefits">{[{id:"voice", Icon:CalendarDays},{id:"avatar", Icon:Fingerprint},{id:"memory", Icon:History}].map(({id,Icon}) => <article key={id}><Icon size={20} aria-hidden="true" /><div><h3>{t(`landing.v4.benefit.${id}.title`)}</h3><p>{t(`landing.v4.benefit.${id}.body`)}</p></div></article>)}</div>
            <button className="landing-web" onClick={() => onOpenWebApp("experience")}>{t("landing.pricing.free.cta")}<ArrowRight size={17} aria-hidden="true" /></button>
          </div>
          <LandingExperience gallery={<LandingCarousel posterLoc={posterLoc} labels={{prev:t("landing.carousel.prev"),next:t("landing.carousel.next"),goTo:(n)=>tf("landing.carousel.goTo",{n:String(n)}),ctaHeadline:t("landing.carousel.cta.headline"),ctaButton:t("landing.carousel.cta.button")}}
            onEndCta={() => { analytics.landingCarouselEndCtaClick(); onOpenWebApp("carousel_end"); }}
            posterSlide={<a href="/app" onClick={(e)=>{e.preventDefault();onOpenWebApp("poster");}} aria-label={t("landing.poster.alt")}><picture><source type="image/avif" srcSet={`/posters/poster-${posterLoc}-470.avif 470w, /posters/poster-${posterLoc}-940.avif 940w`} sizes="(max-width: 480px) 90vw, 360px" /><source type="image/webp" srcSet={`/posters/poster-${posterLoc}-470.webp 470w, /posters/poster-${posterLoc}-940.webp 940w`} sizes="(max-width: 480px) 90vw, 360px" /><img src={`/posters/poster-${posterLoc}-940.jpg`} alt={t("landing.poster.alt")} width={941} height={1672} loading="lazy" decoding="async" className="block h-auto w-full rounded-3xl" /></picture></a>} />} />
        </section>

        <section className="landing-section landing-whatsapp" aria-labelledby="whatsapp-title">
          <div className="landing-whatsapp-mark"><WhatsAppGlyph className="h-12 w-12" /></div>
          <div><p className="landing-eyebrow">WhatsApp</p><h2 id="whatsapp-title">{t("landing.v3.whatsapp.title")}</h2><p>{t("landing.v3.whatsapp.body")}</p></div>
          <div className="landing-whatsapp-action"><button className="landing-outline" onClick={() => onOpenWebApp("whatsapp_section", "whatsapp")}>{t("landing.hero.ctaWhatsapp")}<ArrowRight size={18} aria-hidden="true" /></button><p>{t("landing.hero.ctaWhatsappSub")}</p></div>
        </section>

        <LandingControl />

        <section id="plans" ref={pricingRef} className="landing-section landing-pricing" aria-labelledby="plans-title">
          <LandingPlans onStartFree={() => onOpenWebApp("pricing_free")} onPremium={() => navigate("/pricing")} onStore={() => onDownload("pricing_europe")} />
        </section>

        <section className="landing-section landing-faq" aria-labelledby="faq-title"><div><p className="landing-eyebrow">{t("landing.v3.faq.kicker")}</p><h2 id="faq-title">{t("landing.v3.faq.title")}</h2></div><div>{[1,2,3,4,5,6].map(n=><details key={n}><summary>{t(n <= 3 ? `landing.v3.faq${n}.q` : `landing.v6.faq${n}.q`)}<ChevronDown size={18} aria-hidden="true" /></summary><p>{t(n <= 3 ? `landing.v3.faq${n}.a` : `landing.v6.faq${n}.a`)}</p></details>)}</div></section>

        <section className="landing-final"><p className="landing-eyebrow">VYV · {t("landing.hero.badge")}</p><h2>{t("landing.v3.final.title")}</h2><p>{t("landing.finalCta.sub")}</p><div className="landing-downloads justify-center"><a className="landing-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={()=>onDownload("footer")}><SiApple size={23} aria-hidden="true" />{t("landing.finalCta.download")}</a><a className="landing-store" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={()=>onDownload("footer_play")}><SiGoogleplay size={21} aria-hidden="true" />{t("landing.hero.ctaPlayStore")}</a></div><button className="landing-web" onClick={()=>onOpenWebApp("footer")}>{t("landing.finalCta.web")}<ArrowRight size={17} aria-hidden="true" /></button></section>
      </main>
      <section aria-label={t("landing.tech.heading")} className="landing-tech"><p>{t("landing.tech.heading")}</p><div><span><OpenAIGlyph className="h-6 w-6" />{t("landing.tech.openai")}</span><span><SiGooglegemini aria-hidden="true" />{t("landing.tech.gemini")}</span></div></section>
      <Footer variant="full" />
    </div>
  );
}
